// 4/08/2026
import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Lock, Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck, BarChart3, Activity, Lock as LockIcon } from 'lucide-react'
import { useExecutiveAuthStore } from '../../store/executiveAuthStore'
import { executiveAPI } from '../../services/executiveApi'
import { useToast, getErrorMessage } from '../../components/Toast'

export default function ExecutiveLoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useExecutiveAuthStore()
  const { success: toastSuccess, error: toastError } = useToast()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Guards against a stray second submit while a request is in flight
  const isSubmittingRef = useRef(false)

  const handleLogin = useCallback(
    async () => {
      if (isSubmittingRef.current) return

      if (!username.trim() || !password) {
        toastError('Please enter both username and password.')
        return
      }

      isSubmittingRef.current = true
      setIsLoading(true)

      try {
        const res = await executiveAPI.login(username.trim(), password)

        setAuth(
          { username: res.data.username, full_name: res.data.full_name },
          res.data.token
        )

        toastSuccess('Login successful! Redirecting...')

        setTimeout(() => {
          navigate('/executive/dashboard')
        }, 1500)
      } catch (err: any) {
        // Never clear username/password here, and never navigate/reload.
        const errorMsg = getErrorMessage(err) || 'Invalid credentials. Please check your username and password.'
        toastError(errorMsg)
      } finally {
        isSubmittingRef.current = false
        setIsLoading(false)
      }
    },
    [username, password, setAuth, navigate, toastSuccess, toastError]
  )

  // Prevents default browser submission/navigation immediately
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    void handleLogin()
  }

  return (
    <div className="h-screen w-full overflow-hidden flex flex-col md:flex-row bg-neutral-50">
      {/* Left panel — branding */}
      <div className="relative hidden md:flex md:w-1/2 lg:w-[55%] flex-col justify-between overflow-hidden bg-gradient-to-br from-blue-800 via-blue-900 to-neutral-950 text-white p-10 lg:p-14">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="relative z-10 h-9" />

        <div className="relative z-10 flex items-center gap-3 -mt-8">
          <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0">
            <img src="/logo.png" alt="IRCS Haryana" className="w-11 h-11 object-contain" />
          </div>
          <div>
            <p className="font-semibold leading-tight">Red Cross Society Project</p>
            <p className="text-sm text-blue-100/80 leading-tight">Haryana State Branch</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase mb-5">
            <Activity className="w-3.5 h-3.5" /> Live Monitoring
          </span>
          <h1
            className="text-4xl lg:text-5xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Monitoring Portal
            <br />
            For Executives
          </h1>
          <p className="text-blue-100/80 text-base leading-relaxed">
            A real-time, read-only view of enrollments, certifications, and
            revenue across IRCS Haryana — built for leadership oversight.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          <div className="flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <BarChart3 className="w-5 h-5" />
            </div>
            <p className="text-sm text-blue-100/80">Live Analytics</p>
          </div>
          <div className="flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <Activity className="w-5 h-5" />
            </div>
            <p className="text-sm text-blue-100/80">Auto-Refreshing</p>
          </div>
          <div className="flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <LockIcon className="w-5 h-5" />
            </div>
            <p className="text-sm text-blue-100/80">Read-Only Access</p>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-sm">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 mb-8 transition-colors text-sm font-medium px-3 py-1.5 -ml-3 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> Back to portal selection
          </button>

          {/* Mobile-only header */}
          <div className="md:hidden text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md ring-4 ring-blue-50 mb-3">
              <img src="/logo.png" alt="IRCS Haryana" className="w-12 h-12 object-contain" />
            </div>
            <h1 className="text-xl font-bold text-blue-900" style={{ fontFamily: 'Georgia, serif' }}>
              Red Cross Society Project
            </h1>
            <p className="text-neutral-500 text-sm">Haryana State Branch</p>
          </div>

          <div className="mb-7">
            <h2 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
              Executive Sign In
            </h2>
            <p className="text-sm text-neutral-500 mt-1">Restricted access — authorised executives only.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="executive_admin"
                  autoComplete="username"
                  className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="•••••••••"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  className="absolute right-3 top-2.5 text-neutral-400 hover:text-neutral-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-800 hover:bg-blue-900 disabled:bg-neutral-400 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>

          <div className="flex items-center justify-center gap-2 mt-8 text-neutral-400 text-xs">
            <ShieldCheck className="w-4 h-4" />
            <span>Restricted access · Authorised executives only</span>
          </div>
        </div>
      </div>
    </div>
  )
}


















// import { useState } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { User, Lock, Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck, BarChart3, Activity, Lock as LockIcon } from 'lucide-react'
// import { useExecutiveAuthStore } from '../../store/executiveAuthStore'
// import { executiveAPI } from '../../services/executiveApi'

// export default function ExecutiveLoginPage() {
//   const navigate = useNavigate()
//   const { setAuth } = useExecutiveAuthStore()
//   const [username, setUsername] = useState('')
//   const [password, setPassword] = useState('')
//   const [showPassword, setShowPassword] = useState(false)
//   const [isLoading, setIsLoading] = useState(false)
//   const [error, setError] = useState('')

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault()
//     setError('')
//     if (!username.trim() || !password) {
//       setError('Please enter both username and password.')
//       return
//     }
//     setIsLoading(true)
//     try {
//       const res = await executiveAPI.login(username.trim(), password)
//       setAuth(
//         { username: res.data.username, full_name: res.data.full_name },
//         res.data.token
//       )
//       navigate('/executive/dashboard')
//     } catch (err: any) {
//       setError(err.response?.data?.detail || 'Login failed. Please check your credentials.')
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   return (
//     <div className="h-screen w-full overflow-hidden flex flex-col md:flex-row bg-neutral-50">
//       {/* Left panel — branding */}
//       <div className="relative hidden md:flex md:w-1/2 lg:w-[55%] flex-col justify-between overflow-hidden bg-gradient-to-br from-blue-800 via-blue-900 to-neutral-950 text-white p-10 lg:p-14">
//         <div
//           className="absolute inset-0 opacity-[0.07]"
//           style={{
//             backgroundImage:
//               'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
//             backgroundSize: '32px 32px',
//           }}
//         />

//         {/* Slot 1: back button placeholder — matches staff panel's empty slot so logo row aligns */}
//         <div className="relative z-10 h-9" />

//         <div className="relative z-10 flex items-center gap-3 -mt-8">
//           <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0">
//             <img src="/logo.png" alt="IRCS Haryana" className="w-11 h-11 object-contain" />
//           </div>
//           <div>
//             <p className="font-semibold leading-tight">Red Cross Society Project</p>
//             <p className="text-sm text-blue-100/80 leading-tight">Haryana State Branch</p>
//           </div>
//         </div>

//         <div className="relative z-10 max-w-md">
//           <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase mb-5">
//             <Activity className="w-3.5 h-3.5" /> Live Monitoring
//           </span>
//          <h1
//   className="text-4xl lg:text-5xl font-bold leading-tight mb-4"
//   style={{ fontFamily: 'Georgia, serif' }}
// >
//   Monitoring Portal
//   <br />
//   For Executives
// </h1>
//           <p className="text-blue-100/80 text-base leading-relaxed">
//             A real-time, read-only view of enrollments, certifications, and
//             revenue across IRCS Haryana — built for leadership oversight.
//           </p>
//         </div>

//         <div className="relative z-10 grid grid-cols-3 gap-4">
//           <div className="flex flex-col items-start gap-2">
//             <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
//               <BarChart3 className="w-5 h-5" />
//             </div>
//             <p className="text-sm text-blue-100/80">Live Analytics</p>
//           </div>
//           <div className="flex flex-col items-start gap-2">
//             <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
//               <Activity className="w-5 h-5" />
//             </div>
//             <p className="text-sm text-blue-100/80">Auto-Refreshing</p>
//           </div>
//           <div className="flex flex-col items-start gap-2">
//             <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
//               <LockIcon className="w-5 h-5" />
//             </div>
//             <p className="text-sm text-blue-100/80">Read-Only Access</p>
//           </div>
//         </div>
//       </div>

//       {/* Right panel — login form */}
//       <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
//         <div className="w-full max-w-sm">
//           <button
//             onClick={() => navigate('/')}
//             className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 mb-8 transition-colors text-sm font-medium px-3 py-1.5 -ml-3 rounded-lg"
//           >
//             <ArrowLeft className="w-4 h-4" /> Back to portal selection
//           </button>

//           {/* Mobile-only header */}
//           <div className="md:hidden text-center mb-8">
//             <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md ring-4 ring-blue-50 mb-3">
//               <img src="/logo.png" alt="IRCS Haryana" className="w-12 h-12 object-contain" />
//             </div>
//             <h1 className="text-xl font-bold text-blue-900" style={{ fontFamily: 'Georgia, serif' }}>
//               Red Cross Society Project
//             </h1>
//             <p className="text-neutral-500 text-sm">Haryana State Branch</p>
//           </div>

//           <div className="mb-7">
//             <h2 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
//               Executive Sign In
//             </h2>
//             <p className="text-sm text-neutral-500 mt-1">Restricted access — authorised executives only.</p>
//           </div>

//           <form onSubmit={handleSubmit} className="space-y-4">
//             <div>
//               <label className="block text-sm font-medium text-neutral-700 mb-2">Username</label>
//               <div className="relative">
//                 <User className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
//                 <input
//                   type="text"
//                   value={username}
//                   onChange={(e) => setUsername(e.target.value)}
//                   placeholder="executive_admin"
//                   className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none transition"
//                   required
//                 />
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-neutral-700 mb-2">Password</label>
//               <div className="relative">
//                 <Lock className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
//                 <input
//                   type={showPassword ? 'text' : 'password'}
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   placeholder="•••••••••"
//                   className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none transition"
//                   required
//                 />
//                 <button
//                   type="button"
//                   onClick={() => setShowPassword((v) => !v)}
//                   tabIndex={-1}
//                   className="absolute right-3 top-2.5 text-neutral-400 hover:text-neutral-600"
//                   aria-label={showPassword ? 'Hide password' : 'Show password'}
//                 >
//                   {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
//                 </button>
//               </div>
//             </div>

//             {error && (
//               <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
//                 {error}
//               </div>
//             )}

//             <button
//               type="submit"
//               disabled={isLoading}
//               className="w-full bg-blue-800 hover:bg-blue-900 disabled:bg-neutral-400 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
//             >
//               {isLoading ? (
//                 <>
//                   <Loader2 className="w-4 h-4 animate-spin" />
//                   Signing in...
//                 </>
//               ) : (
//                 'Login'
//               )}
//             </button>
//           </form>

//           <div className="flex items-center justify-center gap-2 mt-8 text-neutral-400 text-xs">
//             <ShieldCheck className="w-4 h-4" />
//             <span>Restricted access · Authorised executives only</span>
//           </div>
//         </div>
//       </div>
//     </div>
//   )
// }

// // import { useState } from 'react'
// // import { useNavigate } from 'react-router-dom'
// // import { User, Lock, Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck, BarChart3, Activity, Lock as LockIcon } from 'lucide-react'
// // import { useExecutiveAuthStore } from '../../store/executiveAuthStore'
// // import { executiveAPI } from '../../services/executiveApi'

// // export default function ExecutiveLoginPage() {
// //   const navigate = useNavigate()
// //   const { setAuth } = useExecutiveAuthStore()
// //   const [username, setUsername] = useState('')
// //   const [password, setPassword] = useState('')
// //   const [showPassword, setShowPassword] = useState(false)
// //   const [isLoading, setIsLoading] = useState(false)
// //   const [error, setError] = useState('')

// //   const handleSubmit = async (e: React.FormEvent) => {
// //     e.preventDefault()
// //     setError('')
// //     if (!username.trim() || !password) {
// //       setError('Please enter both username and password.')
// //       return
// //     }
// //     setIsLoading(true)
// //     try {
// //       const res = await executiveAPI.login(username.trim(), password)
// //       setAuth(
// //         { username: res.data.username, full_name: res.data.full_name },
// //         res.data.token
// //       )
// //       navigate('/executive/dashboard')
// //     } catch (err: any) {
// //       setError(err.response?.data?.detail || 'Login failed. Please check your credentials.')
// //     } finally {
// //       setIsLoading(false)
// //     }
// //   }

// //   return (
// //     <div className="h-screen w-full overflow-hidden flex flex-col md:flex-row bg-neutral-50">
// //       {/* Left panel — branding */}
// //       <div className="relative hidden md:flex md:w-1/2 lg:w-[55%] flex-col justify-between overflow-hidden bg-gradient-to-br from-blue-800 via-blue-900 to-neutral-950 text-white p-10 lg:p-14">
// //         <div
// //           className="absolute inset-0 opacity-[0.07]"
// //           style={{
// //             backgroundImage:
// //               'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
// //             backgroundSize: '32px 32px',
// //           }}
// //         />

// //         <button
// //           onClick={() => navigate('/')}
// //           className="relative z-10 flex items-center gap-2 text-blue-200 hover:text-white transition-colors text-sm w-fit"
// //         >
// //           <ArrowLeft className="w-4 h-4" /> Back to portal selection
// //         </button>

// //         <div className="relative z-10 max-w-md">
// //           <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase mb-5">
// //             <Activity className="w-3.5 h-3.5" /> Live Monitoring
// //           </span>
// //           <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-4">
// //             Executive Dashboard
// //           </h1>
// //           <p className="text-blue-100/80 text-base leading-relaxed">
// //             A real-time, read-only view of enrollments, certifications, and
// //             revenue across IRCS Haryana — built for leadership oversight.
// //           </p>
// //         </div>

// //         <div className="relative z-10 grid grid-cols-3 gap-4">
// //           <div className="flex flex-col items-start gap-2">
// //             <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
// //               <BarChart3 className="w-5 h-5" />
// //             </div>
// //             <p className="text-sm text-blue-100/80">Live Analytics</p>
// //           </div>
// //           <div className="flex flex-col items-start gap-2">
// //             <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
// //               <Activity className="w-5 h-5" />
// //             </div>
// //             <p className="text-sm text-blue-100/80">Auto-Refreshing</p>
// //           </div>
// //           <div className="flex flex-col items-start gap-2">
// //             <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
// //               <LockIcon className="w-5 h-5" />
// //             </div>
// //             <p className="text-sm text-blue-100/80">Read-Only Access</p>
// //           </div>
// //         </div>
// //       </div>

// //       {/* Right panel — login form */}
// //       <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
// //         <div className="w-full max-w-sm">
// //           {/* Mobile-only header */}
// //           <div className="md:hidden mb-6">
// //             <button
// //               onClick={() => navigate('/')}
// //               className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-6 transition-colors text-sm"
// //             >
// //               <ArrowLeft className="w-4 h-4" /> Back to portal selection
// //             </button>
// //             <div className="text-center">
// //               <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md ring-4 ring-blue-50 mb-3">
// //                 <img src="/logo.png" alt="IRCS" className="w-11 h-11 object-contain" />
// //               </div>
// //               <h1 className="text-xl font-bold text-blue-900">Executive Portal</h1>
// //               <p className="text-neutral-500 text-sm">Live Monitoring Dashboard — Read Only</p>
// //             </div>
// //           </div>

// //           <div className="mb-7">
// //             <h2 className="text-2xl font-bold text-neutral-900">Executive Sign In</h2>
// //             <p className="text-sm text-neutral-500 mt-1">Restricted access — authorised executives only.</p>
// //           </div>

// //           <form onSubmit={handleSubmit} className="space-y-4">
// //             <div>
// //               <label className="block text-sm font-medium text-neutral-700 mb-2">Username</label>
// //               <div className="relative">
// //                 <User className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
// //                 <input
// //                   type="text"
// //                   value={username}
// //                   onChange={(e) => setUsername(e.target.value)}
// //                   placeholder="executive_admin"
// //                   className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition"
// //                   required
// //                 />
// //               </div>
// //             </div>

// //             <div>
// //               <label className="block text-sm font-medium text-neutral-700 mb-2">Password</label>
// //               <div className="relative">
// //                 <Lock className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
// //                 <input
// //                   type={showPassword ? 'text' : 'password'}
// //                   value={password}
// //                   onChange={(e) => setPassword(e.target.value)}
// //                   placeholder="•••••••••"
// //                   className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition"
// //                   required
// //                 />
// //                 <button
// //                   type="button"
// //                   onClick={() => setShowPassword((v) => !v)}
// //                   tabIndex={-1}
// //                   className="absolute right-3 top-2.5 text-neutral-400 hover:text-neutral-600"
// //                   aria-label={showPassword ? 'Hide password' : 'Show password'}
// //                 >
// //                   {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
// //                 </button>
// //               </div>
// //             </div>

// //             {error && (
// //               <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
// //                 {error}
// //               </div>
// //             )}

// //             <button
// //               type="submit"
// //               disabled={isLoading}
// //               className="w-full bg-blue-800 hover:bg-blue-900 disabled:bg-neutral-400 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
// //             >
// //               {isLoading ? (
// //                 <>
// //                   <Loader2 className="w-4 h-4 animate-spin" />
// //                   Signing in...
// //                 </>
// //               ) : (
// //                 'Login'
// //               )}
// //             </button>
// //           </form>

// //           <div className="flex items-center justify-center gap-2 mt-8 text-neutral-400 text-xs">
// //             <ShieldCheck className="w-4 h-4" />
// //             <span>Restricted access · Authorised executives only</span>
// //           </div>
// //         </div>
// //       </div>
// //     </div>
// //   )
// // }